import PropTypes from 'prop-types';
import { Box, Typography, Button, ButtonGroup, Popover, Tooltip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { StaticDateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import { Iconify } from 'src/components';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { setReportPracticeIds } from 'src/redux/slices/analyticsReports';

// Quick presets shown as buttons in the bar
const QUICK_PRESETS = [
    { key: 'last24hours', label: 'Last 24 Hours' },
    { key: 'last7days', label: 'Last 7 Days' },
    { key: 'last30days', label: 'Last 30 Days' },
];

// All presets shown in the "More" popover sidebar
const DATE_PRESETS = [
    { key: 'last24hours', label: 'Last 24 Hours' },
    { key: 'last7days', label: 'Last 7 Days' },
    { key: 'last30days', label: 'Last 30 Days' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'lastMonth', label: 'Last Month' },
    { key: 'lastQuarter', label: 'Last Quarter' },
    { key: 'last12months', label: 'Last 12 Months' },
    { key: 'thisYear', label: 'This Year' },
    { key: 'custom', label: 'Custom' },
];

export default function AnalyticsReportsHeader({
    title,
    subtitle,
    tab,
    group = 'Reporting',
    dateRange,
    onDateRangeChange,
    selectedPreset,
    onPresetChange,
    comparisonType,
    onComparisonTypeChange,
    includeToday,
}) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState(null);
    const [customStartDate, setCustomStartDate] = useState(dateRange?.startDate || null);
    const [customEndDate, setCustomEndDate] = useState(dateRange?.endDate || null);

    // The multi-practice selector that used to live here was hidden
    // ahead of the All Practices option-D redesign — a unified global
    // practice scope control replaces it. See docs/superpowers/specs
    // once the design lands. Until then, analytics tabs aggregate
    // across the single practice selected via the global header
    // dropdown (the `practiceTreatmentList.practiceId` source of
    // truth). Reading `reportPracticeIds` is still wired through
    // downstream thunks but with no producer on the UI, it stays null
    // and the app behaves as single-practice.
    const practices = useSelector((state) => state.practiceTreatmentList?.practiceTreatment?.results || []);
    const reportPracticeIds = useSelector((state) => state.analyticsReports.reportPracticeIds);

    // Defensive: reconcile any stale `reportPracticeIds` that a user
    // may have left in localStorage before the selector was hidden.
    // Filters out revoked-practice ids that would otherwise 403 every
    // analytics request. Kept even without the selector because the
    // state can still be populated from localStorage hydration on
    // first load until option D replaces the whole scope model.
    useEffect(() => {
        if (!Array.isArray(reportPracticeIds) || reportPracticeIds.length === 0) return;
        if (practices.length === 0) return;
        const accessibleIds = new Set(practices.map((p) => Number(p.id)));
        const valid = reportPracticeIds.filter((id) => accessibleIds.has(Number(id)));
        if (valid.length !== reportPracticeIds.length) {
            dispatch(setReportPracticeIds(valid.length > 0 ? valid : null));
        }
    }, [reportPracticeIds, practices, dispatch]);

    const handlePresetClick = (preset) => {
        onPresetChange?.(preset);
        if (preset !== 'custom') {
            const ranges = getPresetDateRange(preset);
            onDateRangeChange?.(ranges);
        }
    };

    const handleCustomClick = (event) => {
        // Sync custom dates from the current active dateRange so the picker always
        // opens pre-filled with whatever preset (or custom range) is currently active.
        setCustomStartDate(dateRange?.startDate || null);
        setCustomEndDate(dateRange?.endDate || null);
        setAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const handleApplyCustomRange = () => {
        if (customStartDate && customEndDate) {
            onDateRangeChange?.({
                startDate: customStartDate,
                endDate: customEndDate,
            });
            onPresetChange?.('custom');
        }
        handleClosePopover();
    };

    const getPresetDateRange = (preset) => {
        // Today (in-progress, partial data) is excluded from all "Last N"
        // ranges so the CRM's date filter matches Google Ads' convention:
        // "Last 7 days" = the 7 complete days ending YESTERDAY (12 May →
        // 5–11 May), not 5–12 May. Same applies to last30days / last12months.
        // This also stops the same lead's bookings drifting in/out of
        // "Last N" comparisons depending on what hour of today they
        // happen — only finalised days count.
        // This-month and this-year still run start-of-period → yesterday so
        // partial today isn't double-attributed there either.
        //
        // Exception: when `includeToday` is true (e.g. Call Logs, spec §6.1)
        // presets that would otherwise end yesterday instead end at
        // end-of-today so today's calls are included.
        const today = dayjs();
        const yesterdayEnd = today.subtract(1, 'day').endOf('day');
        const rangeEnd = includeToday ? today.endOf('day') : yesterdayEnd;
        switch (preset) {
            case 'last24hours':
                // "Last 24 Hours" = a true rolling window from this time
                // yesterday to NOW. This is the deliberate exception to the
                // "exclude partial today" rule above — the label promises the
                // last 24 hours, so it must include the in-progress current
                // day. (Today's ad spend is still flagged provisional in the
                // funnel line.)
                return {
                    startDate: today.subtract(24, 'hour').toDate(),
                    endDate: today.toDate(),
                };
            case 'last7days':
                return {
                    startDate: today.subtract(7, 'day').startOf('day').toDate(),
                    endDate: rangeEnd.toDate(),
                };
            case 'last30days':
                return {
                    startDate: today.subtract(30, 'day').startOf('day').toDate(),
                    endDate: rangeEnd.toDate(),
                };
            case 'thisMonth': {
                // On the 1st there are no finalised days in the month yet —
                // yesterdayEnd would sit BEFORE startDate (inverted range →
                // empty reports / 400s). Clamp to today (partial) on day 1.
                const monthStart = today.startOf('month');
                const thisMonthEnd = includeToday ? rangeEnd : yesterdayEnd;
                const thisMonthEndClamped = (!includeToday && yesterdayEnd.isBefore(monthStart)) ? today.endOf('day') : thisMonthEnd;
                return {
                    startDate: monthStart.toDate(),
                    endDate: thisMonthEndClamped.toDate(),
                };
            }
            case 'lastMonth':
                return {
                    startDate: today.subtract(1, 'month').startOf('month').toDate(),
                    endDate: today.subtract(1, 'month').endOf('month').toDate(),
                };
            case 'lastQuarter':
                return {
                    startDate: today.subtract(3, 'month').startOf('month').toDate(),
                    endDate: today.subtract(1, 'month').endOf('month').toDate(),
                };
            case 'last12months':
                return {
                    startDate: today.subtract(12, 'month').startOf('day').toDate(),
                    endDate: rangeEnd.toDate(),
                };
            case 'thisYear': {
                // Same day-1 clamp as thisMonth (1 Jan inverts the range).
                const yearStart = today.startOf('year');
                const thisYearEnd = includeToday ? rangeEnd : yesterdayEnd;
                const thisYearEndClamped = (!includeToday && yesterdayEnd.isBefore(yearStart)) ? today.endOf('day') : thisYearEnd;
                return {
                    startDate: yearStart.toDate(),
                    endDate: thisYearEndClamped.toDate(),
                };
            }
            default:
                return {
                    startDate: today.subtract(30, 'day').startOf('day').toDate(),
                    endDate: rangeEnd.toDate(),
                };
        }
    };

    const open = Boolean(anchorEl);

    // ISS-049: the Schedules & Targets page renders this header for its breadcrumb
    // and the schedule icon only — it has no date-scoped content and passes no date
    // handlers. Rendering the date controls there was misleading (presets silently
    // no-opped through the `?.` calls) and Apply threw
    // `TypeError: onDateRangeChange is not a function`, which unmounted the route.
    // Gate the whole control on the page actually being able to act on a range.
    const hasDateControls = typeof onDateRangeChange === 'function';

    const isQuickPreset = QUICK_PRESETS.some((p) => p.key === selectedPreset);
    const getMoreButtonLabel = () => {
        if (isQuickPreset) return 'Date Range';
        if (selectedPreset === 'custom') return `${dayjs(dateRange?.startDate).format('DD MMM')} — ${dayjs(dateRange?.endDate).format('DD MMM')}`;
        return DATE_PRESETS.find((p) => p.key === selectedPreset)?.label || 'Date Range';
    };

    return (
        // data-page-header: the call pill sits just below the page's header row (CU-869fmcc9r).
        <Box
            data-page-header
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1100,
                bgcolor: (theme) => theme.palette.background.default,
                pb: 2,
                mb: 1,
            }}
        >
            {/* Title row — date-range controls sit inline with the crumb (2026-07-05)
                to reclaim the old full-width date bar's vertical space. Wraps to a
                second line on narrow screens. */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1,
                }}
            >
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        {/* Compact breadcrumb identity (2026-07-04): "Reporting • <tab>" —
                            replaces the big repeated page title; `tab` falls back to the
                            old title render when absent. */}
                        {tab ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 2 }}>
                                    {group}
                                </Typography>
                                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                                <Typography variant='body2' sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.9375rem' }}>
                                    {tab}
                                </Typography>
                            </Box>
                        ) : (
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: '28px',
                                fontWeight: 700,
                            }}
                        >
                            {title}
                        </Typography>
                        )}
                    </Box>
                    {!tab && subtitle && (
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '14px',
                                color: 'text.secondary',
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>

                {/* Date-range controls — inline (the old standalone white bar is gone) */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                        gap: 1,
                        flexShrink: 0,
                        maxWidth: '100%',
                        overflowX: 'auto',
                    }}
                >
                {hasDateControls && (
                <>
                {/* Quick presets + More button in a ButtonGroup */}
                <ButtonGroup variant="outlined" size="small">
                    {QUICK_PRESETS.map((preset) => (
                        <Button
                            key={preset.key}
                            onClick={() => handlePresetClick(preset.key)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                px: 1.5,
                                ...(selectedPreset === preset.key && {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                }),
                            }}
                        >
                            {preset.label}
                        </Button>
                    ))}
                    <Button
                        onClick={handleCustomClick}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 1.5,
                            ...(!isQuickPreset && {
                                bgcolor: 'primary.main',
                                color: 'white',
                                '&:hover': { bgcolor: 'primary.dark' },
                            }),
                        }}
                    >
                        <Iconify icon="eva:calendar-fill" width={16} height={16} sx={{ mr: 0.5 }} />
                        {getMoreButtonLabel()}
                    </Button>
                </ButtonGroup>

                {/* Date popover with presets sidebar + calendar + Compare to */}
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClosePopover}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        sx: {
                            borderRadius: '16px',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                            // ISS-048 — MUI's PopoverPaper defaults to
                            // `overflowY: 'auto'` WITH `maxHeight: calc(100% - 32px)`
                            // (@mui/material Popover.js:88-97). Overriding overflow to
                            // 'hidden' kept the height cap but removed the scroll, so on
                            // a short viewport (1366x768, or 1440x900 at 125% scaling)
                            // everything below the calendar — the read-out, Apply and
                            // Compare to — was clipped with no way to reach it. The
                            // presets stayed clickable because they sit at the top,
                            // which is exactly why the symptom read as "the custom date
                            // range does nothing" rather than "reporting is broken".
                            // 'hidden' stays so the 16px corners still clip the sidebar
                            // fill; the cap and the scrolling now live on the inner
                            // panels, with the Apply footer pinned OUTSIDE the scroll
                            // region so it can never be the thing that gets clipped.
                            overflow: 'hidden',
                            maxHeight: 'calc(100vh - 32px)',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', minWidth: 640, maxHeight: 'calc(100vh - 32px)', minHeight: 0 }}>
                        {/* Left panel: Preset shortcuts */}
                        <Box
                            sx={{
                                width: 180,
                                flexShrink: 0,
                                overflowY: 'auto',
                                borderRight: '1px solid #e2e8f0',
                                py: 1,
                                bgcolor: '#f8fafc',
                            }}
                        >
                            <Typography
                                variant="overline"
                                sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary', fontSize: '10px' }}
                            >
                                Date Range
                            </Typography>
                            {DATE_PRESETS.filter((p) => p.key !== 'custom').map((preset) => (
                                <Box
                                    key={preset.key}
                                    onClick={() => {
                                        handlePresetClick(preset.key);
                                        handleClosePopover();
                                    }}
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: selectedPreset === preset.key ? 600 : 400,
                                        color: selectedPreset === preset.key ? 'primary.main' : 'text.primary',
                                        bgcolor: selectedPreset === preset.key ? '#ede9fe' : 'transparent',
                                        '&:hover': { bgcolor: selectedPreset === preset.key ? '#ede9fe' : '#f1f5f9' },
                                    }}
                                >
                                    {preset.label}
                                </Box>
                            ))}
                        </Box>

                        {/* Right panel: Calendar + controls */}
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0, minWidth: 0 }}>
                            {/* Scroll region: heading + calendar ONLY. Every control the
                                user must be able to click lives below this box, so a short
                                viewport scrolls the calendar instead of hiding Apply. */}
                            <Box sx={{ overflow: 'auto', minHeight: 0, flex: '1 1 auto' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Custom Range
                            </Typography>
                            <StaticDateRangePicker
                                displayStaticWrapperAs="desktop"
                                value={[
                                    customStartDate ? dayjs(customStartDate) : null,
                                    customEndDate ? dayjs(customEndDate) : null,
                                ]}
                                onChange={(newValue) => {
                                    if (newValue[0]) setCustomStartDate(newValue[0].toDate());
                                    if (newValue[1]) setCustomEndDate(newValue[1].toDate());
                                }}
                                renderInput={() => null}
                            />
                            </Box>

                            {/* Date display + Apply button */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, pt: 1, borderTop: '1px solid #e2e8f0' }}>
                                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                    {customStartDate && customEndDate
                                        ? `${dayjs(customStartDate).format('DD MMM YYYY')} — ${dayjs(customEndDate).format('DD MMM YYYY')}`
                                        : 'Select start and end dates'}
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleApplyCustomRange}
                                    disabled={!customStartDate || !customEndDate}
                                    sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
                                >
                                    Apply
                                </Button>
                            </Box>

                            {/* Compare to toggle */}
                            {onComparisonTypeChange && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, pt: 1, borderTop: '1px solid #e2e8f0' }}>
                                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                        Compare to:
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={comparisonType}
                                        exclusive
                                        onChange={(e, value) => value && onComparisonTypeChange(value)}
                                        size="small"
                                        sx={{
                                            '& .MuiToggleButton-root': {
                                                textTransform: 'none',
                                                px: 1.5,
                                                py: 0.25,
                                                fontSize: '11px',
                                                fontWeight: 500,
                                                border: '1px solid #e2e8f0',
                                                '&.Mui-selected': {
                                                    bgcolor: '#10b981',
                                                    color: '#fff',
                                                    borderColor: '#10b981',
                                                    '&:hover': { bgcolor: '#059669' },
                                                },
                                            },
                                        }}
                                    >
                                        <ToggleButton value="off">Off</ToggleButton>
                                        <ToggleButton value="period">Previous Period</ToggleButton>
                                        <ToggleButton value="yoy">Year over Year</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Popover>
                </LocalizationProvider>
                </>
                )}

                {/* Schedule Reports — icon-only, lives at the right of the
                    date-range bar so it sits with the date controls rather
                    than competing with the page title. */}
                <Tooltip title="Schedule automated reports delivered to your inbox" arrow placement="bottom">
                    <Box
                        component="button"
                        type="button"
                        onClick={() => navigate(PATH_DASHBOARD.analyticsReports.settings)}
                        aria-label="Schedule automated reports"
                        sx={{
                            flexShrink: 0,
                            width: 36,
                            height: 36,
                            display: 'grid',
                            placeItems: 'center',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            bgcolor: 'transparent',
                            color: '#475569',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                                borderColor: '#7c3aed',
                                color: '#7c3aed',
                                bgcolor: '#f5f3ff',
                            },
                            '&:focus-visible': {
                                outline: '2px solid #7c3aed',
                                outlineOffset: 2,
                            },
                        }}
                    >
                        <Iconify icon="mdi:calendar-clock-outline" width={18} />
                    </Box>
                </Tooltip>
                </Box>
            </Box>
        </Box>
    );
}

AnalyticsReportsHeader.propTypes = {
    tab: PropTypes.string,
    group: PropTypes.string,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    dateRange: PropTypes.shape({
        startDate: PropTypes.instanceOf(Date),
        endDate: PropTypes.instanceOf(Date),
    }),
    // Optional by design: the Schedules & Targets page renders this header for its
    // breadcrumb and schedule icon only. Absent handlers now hide the date
    // controls (see hasDateControls) instead of rendering dead ones.
    onDateRangeChange: PropTypes.func,
    selectedPreset: PropTypes.string,
    onPresetChange: PropTypes.func,
    comparisonType: PropTypes.oneOf(['off', 'period', 'yoy']),
    onComparisonTypeChange: PropTypes.func,
    /** When true, preset date ranges end at end-of-today instead of end-of-yesterday.
     *  Default false preserves the existing analytics reporting behaviour. */
    includeToday: PropTypes.bool,
};

AnalyticsReportsHeader.defaultProps = {
    subtitle: '',
    dateRange: null,
    onDateRangeChange: null,
    onPresetChange: null,
    selectedPreset: 'last30days',
    comparisonType: 'period',
    onComparisonTypeChange: null,
    includeToday: false,
};
